import {
  Component,
  inject,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import {
  FormsModule,
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators
} from '@angular/forms';
import { isPlatformBrowser, NgClass, NgIf } from '@angular/common';
import { Subscription } from 'rxjs';
import { UtilsService } from '@App/common/services';
import {
  AnalyticsOptionsEnum,
  BusinessStatusEnum,
  PitchCardTypeEnum
} from '@App/common/enums';
import { ScreenSizeService } from '@Shared/services';
import { SmsReferralModel } from '@App/common/models';
import { BusinessService } from '@App/common/services';
import { AppConstantService } from '@Shared/services';
import { AppSettings } from '@Shared/app.settings';
import { CountryInputComponent } from '@App/shared';
import { TranslateModule } from '@ngx-translate/core';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { TooltipModule } from 'primeng/tooltip';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faEnvelope } from '@fortawesome/free-solid-svg-icons';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { AngularXQRCodeComponent } from '@App/modules/pitch-card-shared/components';
import { NoLeadingSpaceDirective } from '@App/common/directives';
import { ActivatedRoute } from '@angular/router';
import { EmbeddedCodeDialogComponent } from '../embedded-code-dialog/embedded-code-dialog.component';

enum IntroduceTypeEnum {
  'text',
  'email'
}

@Component({
  selector: 'app-share-business',
  templateUrl: './share-business.component.html',
  styleUrls: ['./share-business.component.scss'],
  standalone: true,
  imports: [
    AngularXQRCodeComponent,
    ToggleSwitchModule,
    FormsModule,
    FontAwesomeModule,
    TooltipModule,
    InputTextModule,
    DialogModule,
    ReactiveFormsModule,
    TranslateModule,
    CountryInputComponent,
    NgClass,
    NgIf,
    NoLeadingSpaceDirective,
    EmbeddedCodeDialogComponent
  ],
  encapsulation: ViewEncapsulation.None
})
export class ShareBusinessComponent implements OnInit, OnDestroy {
  faEnvelope = faEnvelope;
  private activatedRoute = inject(ActivatedRoute);
  @ViewChild('inputmask') inputMaskRef: any;
  @ViewChild('copyButton') copyButton: any;
  @Input() shareUrl = '';
  @Input() introTextOptions: any;
  @Input() businessId: string;
  @Input() isPocketSharing: boolean = false;
  shareSms: boolean = false;
  submittedSms: boolean = false;
  isMobile: boolean;
  isAvailableIntroduction: boolean;
  smsReferralModel: SmsReferralModel = new SmsReferralModel();
  isBrowser: boolean;
  contactForm: UntypedFormGroup;
  analytics = AnalyticsOptionsEnum;
  status: BusinessStatusEnum;
  $resetCommonShareDialog: Subscription;
  $analyticsSub: Subscription = new Subscription();
  isQrShowed: boolean;
  introduceTypeEnum = IntroduceTypeEnum;
  preparedMessageList: { email: string; messages: string; other: string };
  is3WaySharing: boolean;
  embeddedDialogShow: boolean = false;
  @Input() referralRequestMessage = '';
  @Input() includeReferralInstructions = false;
  @Input() isOwner = false;
  @Input() is3WaySharingDisabled: boolean;

  constructor(
    private formBuilder: UntypedFormBuilder,
    private businessService: BusinessService,
    private utilsService: UtilsService,
    private screenSizeService: ScreenSizeService,
    @Inject(PLATFORM_ID) private platformId: any
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);
  }

  @Input() set inputStatus(status: BusinessStatusEnum) {
    this.status = status ? status : BusinessStatusEnum.Active;
  }

  get form() {
    return this.contactForm.controls;
  }

  get isEmbedded() {
    return this.activatedRoute.snapshot.routeConfig.path.includes(
      'embedded-card'
    );
  }

  get introduceHref(): string | null {
    if (!this.isBrowser) {
      return null;
    }
    const ua = navigator.userAgent || navigator.vendor;
    const isIos = /iPad|iPhone|iPod/i.test(ua);
    const isAndroid = /Android/i.test(ua);
    if (!isIos && !isAndroid) {
      return null;
    }
    const isSmsIntro = this.introTextOptions?.preferredIntroMethod !== 1;
    let { extraPhoneNumber, email } = this.introTextOptions || {
      extraPhoneNumber: null,
      email: null
    };
    extraPhoneNumber = extraPhoneNumber
      .replace(/\D/g, '')
      .replace(/^1?(\d{3})(\d{3})(\d{4})$/, '+1 ($1) $2-$3');

    if (isIos) {
      if (isSmsIntro) {
        const to =
          extraPhoneNumber && this.isAvailableIntroduction && this.is3WaySharing
            ? extraPhoneNumber + ','
            : '';
        return `sms:${to}&body=${this.getEncodedShareBody()}`;
      }
      return `mailto:${this.is3WaySharing ? email || '' : ''}?subject=You've received a PitchCard!&body=${this.getEncodedShareBody()}`;
    }
    if (isAndroid) {
      if (isSmsIntro) {
        const to =
          extraPhoneNumber && this.isAvailableIntroduction && this.is3WaySharing
            ? '+1' + extraPhoneNumber + ','
            : '';
        return `sms:${to}?subject=You've received a PitchCard!&body=${this.getEncodedShareBody()}`;
      }
      return `mailto:${email || ''}?subject=You've received a PitchCard!&body=${this.getEncodedShareBody()}`;
    }
    return null;
  }

  trackIntroduceClick(): void {
    const isSmsIntro = this.introTextOptions?.preferredIntroMethod !== 1;
    this.shareSocialMediaAnalytics(
      isSmsIntro ? this.analytics.ShareText : this.analytics.ShareEmail
    );
  }

  ngOnInit() {
    if (this.isEmbedded) {
      const hostWidth = Number(
        this.activatedRoute.snapshot.queryParamMap.get('width')
      );
      this.isMobile =
        hostWidth > 0 ? hostWidth < 768 : this.screenSizeService.isMobile;
    } else {
      // Preserve the existing behavior for every non-embedded route.
      this.isMobile = this.screenSizeService.isMobile;
    }
    if (this.isBrowser) {
      this.contactForm = this.formBuilder.group({
        contactNumber: [null]
      });
      this.resetCommonShareReferDialog();
    } else {
      // added empty for server side
      this.contactForm = this.formBuilder.group({
        contactNumber: new UntypedFormControl()
      });
    }
    const { isEnabledIntroText } = this.introTextOptions;
    this.isAvailableIntroduction =
      this.isMobile &&
      isEnabledIntroText &&
      this.status === BusinessStatusEnum.Active;
    this.parseIntroText();
    this.is3WaySharing = !this.is3WaySharingDisabled;
  }

  ngOnDestroy() {
    if (this.$resetCommonShareDialog) {
      this.$resetCommonShareDialog.unsubscribe();
    }
    if (this.$analyticsSub) {
      this.$analyticsSub.unsubscribe();
    }
  }

  embededDialog() {
    this.embeddedDialogShow = true;
  }

  get cardAlias(): string {
    if (!this.shareUrl) return '';
    const parts = decodeURIComponent(this.shareUrl).split('/card/');
    return parts.length > 1 ? parts[1] : '';
  }

  parseIntroText() {
    const { name, companyName } = this.introTextOptions;
    let { introText } = this.introTextOptions;
    if (
      introText.includes('<strong>Name</strong>') ||
      introText.includes('<strong>Organization Name</strong>') ||
      introText.includes('<strong>Company Name</strong>')
    ) {
      introText = this.utilsService.encodeExistIntroText({
        companyName,
        name,
        template: introText
      });
    }
    this.introTextOptions.introText =
      this.utilsService.extractHtmlContent(introText);
  }

  sendEmail() {
    if (isPlatformBrowser(this.platformId)) {
      const encoded = this.getEncodedShareBody();
      const a: any = document.createElement('a');
      a.href = `mailto:${this.is3WaySharing ? this.introTextOptions.email : ''}?subject=You've received a ${this.isPocketSharing ? 'Pocket' : 'PitchCard'}!&body=${encoded}`;
      a.click();
    }
    this.shareSocialMediaAnalytics(this.analytics.ShareEmail);
  }

  private getEncodedShareBody(): string {
    if (this.isPocketSharing) {
      const text = `I found some great PitchCards I thought you would be interrested in and wanted to share them with you. I created a Pocket with them in it so you could find them all in one place. Have a great day!${this.shareUrl}`;
      return encodeURIComponent(text);
    }

    const {
      isEnabledIntroText,
      introText,
      includeReferralInstructions,
      referralRequestMessage
    } = this.introTextOptions;
    const refMessage = includeReferralInstructions
      ? encodeURIComponent(
          `${referralRequestMessage.replace(/(<([^>]+)>)/gi, '')} \n\n ${this.shareUrl}`
        )
      : this.shareUrl;

    if (this.isOwner) {
      return refMessage;
    }
    if (isEnabledIntroText) {
      return encodeURIComponent(`${introText} \n\n ${this.shareUrl}`);
    }
    if (includeReferralInstructions) {
      return refMessage;
    }
    return this.shareUrl;
  }

  showSms(isSmsIntro: boolean) {
    if (this.isBrowser) {
      const userAgent = navigator.userAgent || navigator.vendor;

      let { extraPhoneNumber } = this.introTextOptions
        ? this.introTextOptions
        : { extraPhoneNumber: null };

      extraPhoneNumber = extraPhoneNumber
        .replace(/\D/g, '')
        .replace(/^1?(\d{3})(\d{3})(\d{4})$/, '+1 ($1) $2-$3');

      const messageText = this.getEncodedShareBody();
      if (
        userAgent.match(/iPad/i) ||
        userAgent.match(/iPhone/i) ||
        userAgent.match(/iPod/i)
      ) {
        const a: any = document.createElement('a');

        isSmsIntro
          ? (a.href = `sms:${
              extraPhoneNumber && this.isAvailableIntroduction
                ? this.is3WaySharing
                  ? extraPhoneNumber + ','
                  : '' + ','
                : ''
            }&body=${messageText}`)
          : (a.href = `mailto:${this.is3WaySharing ? this.introTextOptions.email : ''}?subject=You've received a ${this.isPocketSharing ? 'Pocket' : 'PitchCard'}!&body=${messageText}`);
        a.click();
      } else if (userAgent.match(/Android/i)) {
        const a: any = document.createElement('a');
        isSmsIntro
          ? (a.href = `sms:${
              extraPhoneNumber &&
              this.isAvailableIntroduction &&
              this.is3WaySharing
                ? '+1' + extraPhoneNumber + ','
                : ''
            }?subject=You've received a ${this.isPocketSharing ? 'Pocket' : 'PitchCard'}!&body=${
              messageText
            }`)
          : (a.href = `mailto:${this.introTextOptions.email}?subject=You've received a ${this.isPocketSharing ? 'Pocket' : 'PitchCard'}!&body=${messageText}`);
        a.click();
      } else {
        this.shareSms = true;
        setTimeout(() => {
          const inputMaskRef: any = this.inputMaskRef;
          if (inputMaskRef?.el?.nativeElement?.children[0]) {
            inputMaskRef.el.nativeElement.children[0].focus();
          }
        }, 300);
      }
    }
    isSmsIntro
      ? this.shareSocialMediaAnalytics(AnalyticsOptionsEnum.ShareText)
      : this.shareSocialMediaAnalytics(AnalyticsOptionsEnum.ShareEmail);
  }

  sendSMSToReferral() {
    if (this.contactForm.invalid) {
      this.submittedSms = true;
      return;
    }
    this.submittedSms = false;
    this.smsReferralModel.contactNumber =
      this.contactForm.controls.contactNumber.value;
    this.smsReferralModel.websiteLink = this.shareUrl;
    this.businessService
      .inviteReferralBySendingSMS(this.smsReferralModel)
      .subscribe({
        next: () => {
          this.shareSms = false;
          this.contactForm.reset();
          this.smsReferralModel.contactNumber = '';
        },
        error: () => {}
      });
  }

  shareSocialMediaAnalytics(analyticsId) {
    if (this.businessId) {
      this.$analyticsSub.add(
        this.businessService
          .shareSocialMediaAnalytics(
            this.businessId ? [this.businessId] : [],
            analyticsId
          )
          .subscribe(() => {})
      );
    }
  }

  onHideShareSmsDialog() {
    this.shareSms = false;
    this.submittedSms = false;
    this.contactForm.get('contactNumber').setValidators([]);
    this.contactForm.get('contactNumber').updateValueAndValidity();
    this.contactForm.reset();
  }

  resetCommonShareReferDialog() {
    this.$resetCommonShareDialog =
      AppConstantService.resetCommonShareDialog.subscribe((data: any) => {
        if (data) {
          this.onHideShareSmsDialog();
        }
      });
  }

  saveQrCode(qrcode) {
    if (qrcode && qrcode.el) {
      const parentElement = qrcode.el.nativeElement.querySelector('img').src;

      // converts base 64 encoded image to blobData
      const blobData = this.convertBase64ToBlob(parentElement);

      if (isPlatformBrowser(this.platformId)) {
        // TODO: need to check and update or remove msSaveOrOpenBlob
        const navigator = window.navigator as any;
        // saves as image
        if (navigator && navigator.msSaveOrOpenBlob) {
          navigator.msSaveOrOpenBlob(blobData, 'Qrcode');
        } else {
          // chrome
          const blob = new Blob([blobData], { type: 'image/png' });
          const url = window.URL.createObjectURL(blob);
          // window.open(url);
          const link = document.createElement('a');
          link.href = url;
          link.download = 'Qrcode';
          link.click();
        }
        this.shareSocialMediaAnalytics(AnalyticsOptionsEnum.ShareQRCode);
      }
    }
  }

  formatPhoneNumber(str) {
    this.contactForm
      .get('contactNumber')
      .setValidators([
        Validators.required,
        Validators.pattern(AppSettings.US_PHONE_PATTERN)
      ]);
    this.contactForm.get('contactNumber').updateValueAndValidity();
    if (str?.length >= 10) {
      this.contactForm
        .get('contactNumber')
        .patchValue(this.utilsService.formatPhoneNumber(str));
    }
  }

  showQR(isShowed: boolean) {
    this.isQrShowed = isShowed;
    this.shareSocialMediaAnalytics(AnalyticsOptionsEnum.ShareQRCode);
  }


  shareTypeChange({ checked }: { checked: boolean }): void {
    this.is3WaySharing = checked;
  }

  introduce() {
    return this.introTextOptions.preferredIntroMethod === 1
      ? this.showSms(false)
      : this.showSms(true);
  }

  share(shareType: string, shareUrl: string) {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    const encodedUrl = encodeURIComponent(shareUrl);
    switch (shareType) {
      case 'x':
        window.open('https://x.com/intent/tweet?url=' + encodedUrl, '_blank');
        this.shareSocialMediaAnalytics(AnalyticsOptionsEnum.ShareTwitter);
        break;
      case 'linkedin':
        window.open(
          'https://www.linkedin.com/sharing/share-offsite/?url=' + encodedUrl,
          '_blank'
        );
        this.shareSocialMediaAnalytics(AnalyticsOptionsEnum.ShareLinkedin);
        break;
      case 'facebook':
        window.open(
          'https://www.facebook.com/sharer/sharer.php?u=' + encodedUrl,
          '_blank'
        );
        this.shareSocialMediaAnalytics(AnalyticsOptionsEnum.ShareFacebook);
        break;
    }
  }

  copyLink() {
    if (!isPlatformBrowser(this.platformId) || !this.shareUrl) {
      return;
    }
    this.copyText(this.shareUrl).then((isCopied) => {
      if (isCopied) {
        this.showCopyButtonFeedback();
        this.showCopySnackbar();
      }
    });
  }

  private showCopyButtonFeedback(): void {
    if (this.copyButton?.nativeElement) {
      const button = this.copyButton.nativeElement as HTMLElement;
      const originalText = button.textContent;
      button.textContent = 'Copied!';
      setTimeout(() => {
        button.textContent = originalText;
      }, 1500);
    }
  }

  private showCopySnackbar(): void {
    document.querySelector('.share-copy-snackbar')?.remove();

    const snackbar = document.createElement('div');
    snackbar.className = 'share-copy-snackbar';
    snackbar.textContent = 'Link copied to clipboard.';
    snackbar.setAttribute('role', 'status');
    snackbar.setAttribute('aria-live', 'polite');
    document.body.appendChild(snackbar);

    setTimeout(() => snackbar.remove(), 2500);
  }

  private async copyText(text: string): Promise<boolean> {
    if (!this.isBrowser || !text) {
      return false;
    }

    if (navigator.clipboard?.writeText) {
      try {
        await navigator.clipboard.writeText(text);
        return true;
      } catch {
        // Fall through to legacy copy approach when Clipboard API is blocked.
      }
    }

    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    textarea.style.pointerEvents = 'none';
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();
    textarea.setSelectionRange(0, textarea.value.length);

    try {
      return document.execCommand('copy');
    } catch {
      return false;
    } finally {
      document.body.removeChild(textarea);
    }
  }

  private convertBase64ToBlob(Base64Image: any) {
    if (!isPlatformBrowser(this.platformId)) {
      return null;
    }
    // SPLIT INTO TWO PARTS
    const parts = Base64Image.split(';base64,');
    // HOLD THE CONTENT TYPE
    const imageType = parts[0].split(':')[1];
    // DECODE BASE64 STRING
    const decodedData = window.atob(parts[1]);
    // CREATE UNIT8ARRAY OF SIZE SAME AS ROW DATA LENGTH
    const uInt8Array = new Uint8Array(decodedData.length);
    // INSERT ALL CHARACTER CODE INTO UINT8ARRAY
    for (let i = 0; i < decodedData.length; ++i) {
      uInt8Array[i] = decodedData.charCodeAt(i);
    }
    // RETURN BLOB IMAGE AFTER CONVERSION
    return new Blob([uInt8Array], { type: imageType });
  }
}
